import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { jobsApi, uploadApi } from '../services/api';
import { showToast } from 'shared-ui';

interface JobListing {
  id: string;
  businessId: string;
  businessName: string;
  businessMangoId: string;
  businessLogoUrl?: string;
  title: string;
  description: string;
  employmentType: string;
  location: string;
  isRemote: boolean;
  salary?: { min: number; max: number; type: string };
  skills: string[];
  category: string;
  postedAt: string;
  applicantCount: number;
}

interface JobProfile {
  headline: string;
  isLookingForWork: boolean;
  preferredHours: string;
  skills: string[];
  workHistory: { company: string; title: string; current: boolean }[];
  resumeUrl?: string;
}

const JOB_CATEGORIES = [
  'All Categories',
  'Hospitality & Food Service',
  'Retail & Sales',
  'Creative & Design',
  'Technology',
  'Administration',
  'Health & Wellness',
  'Education',
  'Trades & Construction',
  'Finance & Accounting',
  'Marketing',
];

const EMPLOYMENT_TYPES = [
  { value: 'all', label: 'All Types' },
  { value: 'full_time', label: 'Full Time' },
  { value: 'part_time', label: 'Part Time' },
  { value: 'casual', label: 'Casual' },
  { value: 'contract', label: 'Contract' },
  { value: 'internship', label: 'Internship' },
];

const Jobs: React.FC = () => {
  useNavigate();
  useAuth();
  const [activeTab, setActiveTab] = useState<'browse' | 'applications' | 'profile'>('browse');
  const [jobs, setJobs] = useState<JobListing[]>([]);
  const [, setApplications] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [category, setCategory] = useState('All Categories');
  const [employmentType, setEmploymentType] = useState('all');
  const [remoteOnly, setRemoteOnly] = useState(false);
  
  const [profile, setProfile] = useState<JobProfile>({
    headline: '',
    isLookingForWork: false,
    preferredHours: 'any',
    skills: [],
    workHistory: [],
  });
  
  const [, setShowProfileModal] = useState(false);
  const [uploadingResume, setUploadingResume] = useState(false);
  const [resumeMessage, setResumeMessage] = useState<string | null>(null);
  const [profileMessage, setProfileMessage] = useState<{ ok: boolean; text: string } | null>(null);
  const [savingProfile, setSavingProfile] = useState(false);

  useEffect(() => {
    loadJobs();
    loadProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load on mount only
  }, []);

  const loadJobs = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Build filters (API expects type, not employmentType)
      const filters: any = {};
      if (searchQuery) filters.query = searchQuery;
      if (category !== 'All Categories') filters.category = category;
      if (employmentType !== 'all') filters.type = employmentType;
      if (remoteOnly) filters.remote = true;
      
      const response = await jobsApi.searchJobs(filters);
      
      const list = response?.data ?? response?.postings ?? [];
      setJobs(Array.isArray(list) ? list : []);
      if (!response?.success && response?.error) setError(response.error);
    } catch (err) {
      console.error('Failed to load jobs:', err);
      setError('Failed to load jobs. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  const loadProfile = async () => {
    try {
      const response = await jobsApi.getMyProfile();
      if (response.success && response.data) {
        setProfile({ ...response.data, workHistory: response.data.workHistory || [], skills: response.data.skills || [] });
      }
    } catch (err) {
      console.error('Failed to load profile:', err);
    }
  };
  
  const loadApplications = async () => {
    try {
      const response = await jobsApi.getMyApplications();
      if (response.success) {
        setApplications(response.data || []);
      }
    } catch (err) {
      console.error('Failed to load applications:', err);
    }
  };
  
  const handleApply = async (jobId: string, coverLetter?: string) => {
    try {
      const response = await jobsApi.applyForJob(jobId, {
        coverLetter,
        resumeUrl: profile.resumeUrl,
      });
      if (response.success) {
        loadApplications();
        showToast('Application submitted!', 'success');
      }
    } catch (err) {
      console.error('Failed to apply:', err);
      showToast('Failed to submit application', 'error');
    }
  };
  
  /** Saves the editable fields (work history is read-only - it comes from Mango employers). */
  const handleUpdateProfile = async (newProfile: JobProfile): Promise<boolean> => {
    setSavingProfile(true);
    try {
      const response = await jobsApi.updateProfile({
        headline: newProfile.headline,
        isLookingForWork: newProfile.isLookingForWork,
        preferredHours: newProfile.preferredHours,
        skills: newProfile.skills,
        resumeUrl: newProfile.resumeUrl || null,
      });
      if (!response.success) throw new Error(response.error || 'Could not save your profile');
      setProfile({ ...response.data, workHistory: response.data.workHistory || [] });
      setShowProfileModal(false);
      setProfileMessage({ ok: true, text: 'Profile saved.' });
      return true;
    } catch (err) {
      setProfileMessage({ ok: false, text: err instanceof Error ? err.message : 'Could not save your profile' });
      return false;
    } finally {
      setSavingProfile(false);
    }
  };

  /** The Open to Work switch saves straight away. */
  const toggleOpenToWork = async (open: boolean) => {
    const before = profile.isLookingForWork;
    setProfile({ ...profile, isLookingForWork: open });
    try {
      const response = await jobsApi.setOpenToWork(open);
      if (!response.success) throw new Error(response.error || 'Could not update');
      setProfileMessage({ ok: true, text: open ? 'Open to work is on.' : 'Open to work is off.' });
    } catch (err) {
      setProfile((p) => ({ ...p, isLookingForWork: before }));
      setProfileMessage({ ok: false, text: err instanceof Error ? err.message : 'Could not update' });
    }
  };

  // NOTE: Demo data removed - using live API data

  const saveProfile = () => {
    handleUpdateProfile(profile);
  };

  const handleResumeUpload = async (file: File) => {
    if (!file) return;
    setUploadingResume(true);
    setResumeMessage(null);
    try {
      const response = await uploadApi.uploadFile(file, 'document');
      const uploadedUrl =
        response?.url ||
        response?.fileUrl ||
        response?.data?.url ||
        response?.data?.fileUrl ||
        response?.data?.path ||
        response?.path;

      if (!uploadedUrl) {
        setResumeMessage('Resume upload failed: file URL not returned.');
        return;
      }

      const updatedProfile = { ...profile, resumeUrl: String(uploadedUrl) };
      setProfile(updatedProfile);
      const saved = await handleUpdateProfile(updatedProfile);
      setResumeMessage(saved ? 'Resume uploaded and saved to your profile.' : 'Resume upload failed: could not save it to your profile.');
    } catch (error) {
      console.error('Resume upload failed:', error);
      setResumeMessage('Failed to upload resume.');
    } finally {
      setUploadingResume(false);
    }
  };

  // Filter jobs
  let filteredJobs = [...jobs];
  
  if (searchQuery) {
    const q = searchQuery.toLowerCase();
    filteredJobs = filteredJobs.filter(j => 
      j.title.toLowerCase().includes(q) ||
      j.businessName.toLowerCase().includes(q) ||
      j.skills.some(s => s.toLowerCase().includes(q))
    );
  }
  
  if (category !== 'All Categories') {
    filteredJobs = filteredJobs.filter(j => j.category === category);
  }
  
  if (employmentType !== 'all') {
    filteredJobs = filteredJobs.filter(j => j.employmentType === employmentType);
  }
  
  if (remoteOnly) {
    filteredJobs = filteredJobs.filter(j => j.isRemote);
  }

  const formatSalary = (salary: { min: number; max: number; type: string }) => {
    const formatNum = (n: number) => n >= 1000 ? `$${(n / 1000).toFixed(0)}k` : `$${n}`;
    return `${formatNum(salary.min)} - ${formatNum(salary.max)} ${salary.type}`;
  };

  const formatEmploymentType = (type: string) => {
    return type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  return (
    <div>
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        borderRadius: '16px',
        padding: '35px',
        marginBottom: '25px',
        color: 'white'
      }}>
        <h1 style={{ margin: '0 0 10px 0', fontSize: '28px' }}>💼 Mango Jobs</h1>
        <p style={{ margin: '0 0 20px 0', opacity: 0.9 }}>
          Find opportunities at Mango businesses or get discovered by employers
        </p>
        
        <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap' }}>
          <div style={{
            padding: '12px 20px',
            background: 'rgba(255,255,255,0.15)',
            borderRadius: '10px',
            fontSize: '14px'
          }}>
            🏢 {jobs.length} Open Positions
          </div>
          <div style={{
            padding: '12px 20px',
            background: 'rgba(255,255,255,0.15)',
            borderRadius: '10px',
            fontSize: '14px'
          }}>
            ✓ Verified Mango Businesses
          </div>
          <div style={{
            padding: '12px 20px',
            background: 'rgba(255,255,255,0.15)',
            borderRadius: '10px',
            fontSize: '14px'
          }}>
            📱 Apply with Mango Profile
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{
        display: 'flex',
        gap: '10px',
        marginBottom: '25px'
      }}>
        {[
          { id: 'browse', label: '🔍 Browse Jobs' },
          { id: 'applications', label: '📋 My Applications' },
          { id: 'profile', label: '👤 My Job Profile' },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id as any)}
            style={{
              padding: '12px 24px',
              background: activeTab === tab.id 
                ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                : 'white',
              color: activeTab === tab.id ? 'white' : '#333',
              border: activeTab === tab.id ? 'none' : '1px solid #e0e0e0',
              borderRadius: '10px',
              cursor: 'pointer',
              fontWeight: activeTab === tab.id ? '600' : '400',
              fontSize: '14px'
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Browse Jobs Tab */}
      {activeTab === 'browse' && (
        <>
          {/* Filters */}
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '20px',
            marginBottom: '20px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            display: 'flex',
            gap: '15px',
            flexWrap: 'wrap',
            alignItems: 'center'
          }}>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search jobs, companies, skills..."
              style={{
                flex: '1 1 250px',
                padding: '12px 16px',
                border: '1px solid #e0e0e0',
                borderRadius: '8px',
                fontSize: '14px'
              }}
            />
            
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              style={{
                padding: '12px 16px',
                border: '1px solid #e0e0e0',
                borderRadius: '8px',
                fontSize: '14px'
              }}
            >
              {JOB_CATEGORIES.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
            
            <select
              value={employmentType}
              onChange={(e) => setEmploymentType(e.target.value)}
              style={{
                padding: '12px 16px',
                border: '1px solid #e0e0e0',
                borderRadius: '8px',
                fontSize: '14px'
              }}
            >
              {EMPLOYMENT_TYPES.map(type => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
            
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={remoteOnly}
                onChange={(e) => setRemoteOnly(e.target.checked)}
              />
              <span style={{ fontSize: '14px' }}>🏠 Remote only</span>
            </label>
          </div>

          {/* Job Listings */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
            {loading ? (
              <div style={{ textAlign: 'center', padding: '60px' }}>
                <p style={{ color: '#666' }}>Loading jobs...</p>
              </div>
            ) : filteredJobs.length === 0 ? (
              <div style={{ 
                background: 'white', 
                padding: '60px', 
                borderRadius: '16px', 
                textAlign: 'center' 
              }}>
                <span style={{ fontSize: '48px' }}>💼</span>
                <h3 style={{ margin: '15px 0 8px 0' }}>No jobs found</h3>
                <p style={{ color: '#666' }}>Try adjusting your filters</p>
              </div>
            ) : (
              filteredJobs.map(job => (
                <div
                  key={job.id}
                  style={{
                    background: 'white',
                    borderRadius: '16px',
                    padding: '25px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    display: 'flex',
                    gap: '20px',
                    alignItems: 'start'
                  }}
                >
                  {/* Company Logo */}
                  <div style={{
                    width: '60px',
                    height: '60px',
                    borderRadius: '12px',
                    background: 'linear-gradient(135deg, #667eea, #764ba2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: '20px',
                    fontWeight: '700',
                    flexShrink: 0
                  }}>
                    {job.businessName.substring(0, 2).toUpperCase()}
                  </div>

                  {/* Job Info */}
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                      <div>
                        <h3 style={{ margin: 0, fontSize: '18px', color: '#333' }}>{job.title}</h3>
                        <p style={{ margin: '6px 0 0 0', fontSize: '14px', color: '#667eea' }}>
                          {job.businessName} • {job.businessMangoId}
                        </p>
                      </div>
                      {job.salary && (
                        <div style={{ textAlign: 'right' }}>
                          <p style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: '#28a745' }}>
                            {formatSalary(job.salary)}
                          </p>
                        </div>
                      )}
                    </div>

                    <p style={{ 
                      margin: '12px 0', 
                      fontSize: '14px', 
                      color: '#666',
                      lineHeight: 1.5
                    }}>
                      {job.description}
                    </p>

                    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '15px' }}>
                      <span style={{
                        padding: '5px 12px',
                        background: '#667eea20',
                        color: '#667eea',
                        borderRadius: '6px',
                        fontSize: '12px',
                        fontWeight: '600'
                      }}>
                        {formatEmploymentType(job.employmentType)}
                      </span>
                      <span style={{
                        padding: '5px 12px',
                        background: job.isRemote ? '#28a74520' : '#f0f0f0',
                        color: job.isRemote ? '#28a745' : '#666',
                        borderRadius: '6px',
                        fontSize: '12px'
                      }}>
                        📍 {job.location}
                      </span>
                      {job.skills.slice(0, 3).map((skill, i) => (
                        <span key={i} style={{
                          padding: '5px 12px',
                          background: '#f0f0f0',
                          color: '#666',
                          borderRadius: '6px',
                          fontSize: '12px'
                        }}>
                          {skill}
                        </span>
                      ))}
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ fontSize: '12px', color: '#888' }}>
                        Posted {new Date(job.postedAt).toLocaleDateString()} • {job.applicantCount} applicants
                      </div>
                      <button
                        onClick={() => handleApply(job.id)}
                        style={{
                          padding: '10px 24px',
                          background: 'linear-gradient(135deg, #667eea, #764ba2)',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          fontWeight: '600',
                          fontSize: '13px'
                        }}
                      >
                        Apply Now
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </>
      )}

      {/* My Applications Tab */}
      {activeTab === 'applications' && (
        <div style={{ 
          background: 'white', 
          padding: '60px', 
          borderRadius: '16px', 
          textAlign: 'center' 
        }}>
          <span style={{ fontSize: '48px' }}>📋</span>
          <h3 style={{ margin: '15px 0 8px 0' }}>No applications yet</h3>
          <p style={{ color: '#666', margin: '0 0 20px 0' }}>
            When you apply to jobs, they'll appear here
          </p>
          <button
            onClick={() => setActiveTab('browse')}
            style={{
              padding: '12px 24px',
              background: 'linear-gradient(135deg, #667eea, #764ba2)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: '600'
            }}
          >
            Browse Jobs
          </button>
        </div>
      )}

      {/* My Profile Tab */}
      {activeTab === 'profile' && (
        <div style={{ maxWidth: '700px' }}>
          {/* Looking for Work Toggle */}
          <div style={{
            background: profile.isLookingForWork 
              ? 'linear-gradient(135deg, #28a74520, #20c99720)'
              : 'white',
            border: profile.isLookingForWork ? '2px solid #28a745' : '1px solid #e0e0e0',
            borderRadius: '16px',
            padding: '25px',
            marginBottom: '20px'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '16px', color: '#333' }}>
                  🟢 Open to Work
                </h3>
                <p style={{ margin: '8px 0 0 0', fontSize: '14px', color: '#666' }}>
                  Saved on your private job profile. Your current employer can&apos;t see it.
                </p>
              </div>
              <label style={{ 
                position: 'relative',
                display: 'inline-block',
                width: '56px',
                height: '30px'
              }}>
                <input
                  type="checkbox"
                  checked={profile.isLookingForWork}
                  onChange={(e) => toggleOpenToWork(e.target.checked)}
                  style={{ opacity: 0, width: 0, height: 0 }}
                />
                <span style={{
                  position: 'absolute',
                  cursor: 'pointer',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: profile.isLookingForWork ? '#28a745' : '#ccc',
                  borderRadius: '30px',
                  transition: '0.3s'
                }}>
                  <span style={{
                    position: 'absolute',
                    height: '24px',
                    width: '24px',
                    left: profile.isLookingForWork ? '28px' : '3px',
                    bottom: '3px',
                    background: 'white',
                    borderRadius: '50%',
                    transition: '0.3s'
                  }} />
                </span>
              </label>
            </div>
          </div>

          {/* Profile Form */}
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: '25px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
          }}>
            <h3 style={{ margin: '0 0 20px 0', fontSize: '18px', color: '#333' }}>
              👤 Your Job Profile
            </h3>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '14px' }}>
                Professional Headline
              </label>
              <input
                type="text"
                value={profile.headline}
                onChange={(e) => setProfile({ ...profile, headline: e.target.value })}
                placeholder="e.g., Experienced Barista & Coffee Enthusiast"
                style={{
                  width: '100%',
                  padding: '14px',
                  border: '1px solid #e0e0e0',
                  borderRadius: '10px',
                  fontSize: '15px'
                }}
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '14px' }}>
                Preferred Work Type
              </label>
              <select
                value={profile.preferredHours}
                onChange={(e) => setProfile({ ...profile, preferredHours: e.target.value })}
                style={{
                  width: '100%',
                  padding: '14px',
                  border: '1px solid #e0e0e0',
                  borderRadius: '10px',
                  fontSize: '15px'
                }}
              >
                <option value="any">Any</option>
                <option value="full_time">Full Time</option>
                <option value="part_time">Part Time</option>
                <option value="casual">Casual</option>
                <option value="contract">Contract</option>
              </select>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '14px' }}>
                Skills (comma separated)
              </label>
              <input
                type="text"
                value={profile.skills.join(', ')}
                onChange={(e) => setProfile({ ...profile, skills: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })}
                placeholder="e.g., Customer Service, Barista, Leadership"
                style={{
                  width: '100%',
                  padding: '14px',
                  border: '1px solid #e0e0e0',
                  borderRadius: '10px',
                  fontSize: '15px'
                }}
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '14px' }}>
                Resume (PDF/DOC)
              </label>
              <input
                type="file"
                accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleResumeUpload(file);
                }}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #e0e0e0',
                  borderRadius: '10px',
                  fontSize: '14px',
                  background: '#fff',
                }}
              />
              {uploadingResume && <p style={{ margin: '8px 0 0 0', fontSize: '13px', color: '#666' }}>Uploading resume...</p>}
              {resumeMessage && <p style={{ margin: '8px 0 0 0', fontSize: '13px', color: resumeMessage.toLowerCase().includes('failed') ? '#b91c1c' : '#166534' }}>{resumeMessage}</p>}
              {profile.resumeUrl && (
                <a
                  href={profile.resumeUrl}
                  target="_blank"
                  rel="noreferrer"
                  style={{ display: 'inline-block', marginTop: '8px', fontSize: '13px', color: '#4f46e5' }}
                >
                  View current resume
                </a>
              )}
            </div>

            {profileMessage && (
              <p role="status" style={{ margin: '0 0 12px 0', fontSize: '14px', color: profileMessage.ok ? '#166534' : '#b91c1c' }}>{profileMessage.text}</p>
            )}
            <button
              onClick={saveProfile}
              disabled={savingProfile}
              style={{
                width: '100%',
                padding: '14px',
                background: 'linear-gradient(135deg, #667eea, #764ba2)',
                color: 'white',
                border: 'none',
                borderRadius: '10px',
                fontWeight: '600',
                fontSize: '15px',
                cursor: 'pointer'
              }}
            >
              {savingProfile ? 'Saving…' : 'Save Profile'}
            </button>
          </div>

          {/* Work History (Mango Verified) */}
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: '25px',
            marginTop: '20px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
          }}>
            <h3 style={{ margin: '0 0 15px 0', fontSize: '18px', color: '#333' }}>
              🏢 Work History (Mango Verified)
            </h3>
            <p style={{ margin: '0 0 20px 0', fontSize: '14px', color: '#666' }}>
              Work experience at Mango businesses is automatically verified and displayed here.
            </p>
            {profile.workHistory.length > 0 ? (
              <div style={{ display: 'grid', gap: '10px' }}>
                {profile.workHistory.map((w, i) => (
                  <div key={i} style={{ padding: '14px 16px', background: '#f8f9fa', borderRadius: '12px', display: 'flex', justifyContent: 'space-between', gap: '10px' }}>
                    <div>
                      <div style={{ fontWeight: 600, color: '#333' }}>{w.company}</div>
                      <div style={{ fontSize: '13px', color: '#666', textTransform: 'capitalize' }}>{w.title}</div>
                    </div>
                    <span style={{ fontSize: '12px', color: w.current ? '#166534' : '#888', fontWeight: 600 }}>{w.current ? '✓ Current' : '✓ Verified'}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ padding: '30px', background: '#f8f9fa', borderRadius: '12px', textAlign: 'center' }}>
                <span style={{ fontSize: '32px' }}>✓</span>
                <p style={{ margin: '10px 0 0 0', color: '#888', fontSize: '14px' }}>
                  No verified Mango work history yet
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Jobs;
